import { useMemo } from 'react';
import type {
  AIAnalysisFinding,
  AIAnalysisArtifact,
} from '../types/agenticTriageReport';
import type { ProcessedArtifact } from './useProcessArtifacts';

interface UseParseAnalysisFindingsParams {
  details: string[];
  artifacts: ProcessedArtifact[];
}

interface UseParseAnalysisFindingsReturn {
  findings: AIAnalysisFinding[];
  artifacts: AIAnalysisArtifact[];
}

/**
 * Parses plain text details and highlights entities as artifacts
 *
 * This hook transforms the analysis_summary_section.details array (plain strings)
 * into findings with artifact references for interactive highlighting.
 *
 * When the API provides findings/artifacts directly, this hook can be removed.
 *
 * @param details - Array of analysis detail strings from the API
 * @param artifacts - Processed artifacts with RegExp patterns for matching
 * @returns Findings with artifact highlights and artifact metadata
 */
export const useParseAnalysisFindings = ({
  details,
  artifacts,
}: UseParseAnalysisFindingsParams): UseParseAnalysisFindingsReturn => {
  return useMemo(() => {
    if (!details.length) return { findings: [], artifacts: [] };
    // Parse each detail string and create findings with artifact highlights
    const findings: AIAnalysisFinding[] = details.map((detail, index) => {
      const content: Array<string | { artifactId: string }> = [];
      let remainingText = detail;
      let lastIndex = 0;

      // Find all matches for all patterns and sort by position
      const matches: Array<{
        index: number;
        length: number;
        artifactId: string;
      }> = [];

      artifacts.forEach((artifact) => {
        let match;
        // Use the artifact's pattern directly if it has the global flag
        const regex = artifact.pattern;
        // Reset lastIndex to ensure we start from the beginning
        if (regex.global) {
          regex.lastIndex = 0;
        }
        while ((match = regex.exec(detail)) !== null) {
          matches.push({
            index: match.index,
            length: match[0].length,
            artifactId: artifact.id,
          });
        }
      });

      // Sort matches by position
      matches.sort((a, b) => a.index - b.index);

      // Build content array with text and artifact references
      matches.forEach((match) => {
        // Add text before this match
        if (match.index > lastIndex) {
          content.push(remainingText.slice(0, match.index - lastIndex));
          remainingText = remainingText.slice(match.index - lastIndex);
        }

        // Add artifact reference
        content.push({ artifactId: match.artifactId });

        // Move past this match
        remainingText = remainingText.slice(match.length);
        lastIndex = match.index + match.length;
      });

      // Add any remaining text
      if (remainingText.length > 0) {
        content.push(remainingText);
      }

      // If no artifacts were found, just use the plain text
      if (content.length === 0) {
        content.push(detail);
      }

      return {
        id: `finding-${index}`,
        content,
      };
    });

    // Only include artifacts that were actually referenced
    const usedArtifactIds = new Set<string>();
    findings.forEach((finding) => {
      finding.content.forEach((item) => {
        if (typeof item === 'object' && 'artifactId' in item) {
          usedArtifactIds.add(item.artifactId);
        }
      });
    });

    const parsedArtifacts = artifacts
      .filter((def) => usedArtifactIds.has(def.id))
      .map((def) => ({
        id: def.id,
        label: def.label,
        title: def.title,
        description: def.description,
        evidenceLink: def.evidenceLink,
      }));

    return { findings, artifacts: parsedArtifacts };
  }, [details, artifacts]);
};
